import {ReduxList, getSyncStatus} from '../../_libs/redux-observable-list';
import {sortByProperty} from '../../core-utilities/filters/filterUtils';
import {makeActionCreator, makePayloadActionCreator} from '../../core-utilities/redux';
import {SYNC_MODEL_PATH, SYNC_PATH} from '../../core-utilities/rx-utils';
import {BASE_REDUX_LIST_OPTIONS} from '../../config/redux-list';
import {buildParentChannelSyncNotifier} from '../../auth/utils/redux-lists';
import {queueIdGenerator} from '../actions/chatMessageQueueActions';
import {buildListSupportEpics} from '../epics/listSupportEpics';
import {getChatMessages} from '../reducers/state';
import {CHAT_MESSAGE_LIST_PREFIX} from './state';
import {logger} from './logger';

/**
 * @typedef {ReduxObservableListState} ChatMessagesListState
 * @property {Boolean} isLoadingReadCount
 * @property {Number} readCount
 */

const parentPath = 'chat-channels';
const resourcePath = `chat-messages`; // may need this for sync to post functionality
const childPath = 'messages';

const postTextMessage = makePayloadActionCreator(CHAT_MESSAGE_LIST_PREFIX, {
  payload: payload => {
    payload.requestId = queueIdGenerator();
    payload.type = 'TEXT';
    payload.date = Date.now();
    return payload;
  },
  type: 'POST_TEXT_MESSAGE',
});

const updateReadCount = makeActionCreator(CHAT_MESSAGE_LIST_PREFIX, 'UPDATE_READ_COUNT');

export const reduxList = new ReduxList({
  ...BASE_REDUX_LIST_OPTIONS,
  prefix: CHAT_MESSAGE_LIST_PREFIX,
  getState: getChatMessages,
  pageSize: 20,
  hasParentId: true,
  useSyncUpdates: true,
  canSyncCreate: true,
  useDelete: false,
  useFiltering: false,
  useAutoFilter: false,
  useFilterOptions: false,
  useSortOptions: false,
  applySortOptions,
  syncNotifier: buildParentChannelSyncNotifier('conversations', childPath, `chat-messages syncNotifier`),
  syncModelPath: parentId => `${parentPath}/${parentId}/${childPath}/${SYNC_MODEL_PATH}`,
  listPath: parentId => `${parentPath}/${parentId}/${childPath}`,
  listSyncPath: parentId => `${parentPath}/${parentId}/${childPath}/${SYNC_PATH}`,
  syncModelActionsTypes: [postTextMessage.TYPE],
  initialState: {
    isLoadingReadCount: true,
    readCount: null,
  },
  logger,
});

export function applySortOptions(list) {
  return list.sort(sortByProperty('date', true, true));
}

reduxList.actions.postTextMessage = postTextMessage;
reduxList.actions.updateReadCount = updateReadCount;
reduxList.reducers.reducer = buildReducers(reduxList);

const supportEpics = buildListSupportEpics({
  actions: reduxList.actions,
  getState: getChatMessages,
  logger,
});

reduxList.epics.addEpics(supportEpics);

// TODO: implement channel notification link
// TODO: start on image upload system

/**
 * @typedef {Object} PostTextMessagePayload
 * @property {String} requestId
 * @property {String} channel
 * @property {String} message
 * @property {Array<Object>} callouts
 * @property {Number} date
 */

/**
 * @typedef {Object} UpdateReadCountPayload
 * @property {Number} readCount
 */

function buildReducers(reduxList) {

  const baseReducer = reduxList.reducers.reducer;
  const initialState = reduxList.reducers.initialState;

  const getListId = reduxList.reducers.getListId;
  const hasValidParentId = reduxList.reducers.hasValidParentId;
  const updatesReducer = reduxList.reducers.updatesReducer;

  return function (state = initialState, action) {
    switch (action.type) {
      case reduxList.actions.postTextMessage.TYPE:
        return postTextMessageReducer(state, action);

      case reduxList.actions.updateReadCount.TYPE:
        return updateReadCountReducer(state, action);

      default:
        return baseReducer(state, action);
    }
  };

  /**
   * @param {ChatMessagesListState} state
   * @param {ReduxAction<PostTextMessagePayload>} action
   * @return {ChatMessagesListState}
   */
  function postTextMessageReducer(state, action) {
    /** @type {PostTextMessagePayload} */
    const payload = action.payload;

    // TODO: we need a way for hasParentId == true lists to manage syncing data when the list is switched
    // this would be solved if getState(rootState, parentId) proves to be a viable approach
    if (payload.channel !== state.parentId) {
      return state;
    }

    const syncStatus = getSyncStatus(state.updates[payload.requestId]);
    const newItem = {
      requestId: payload.requestId,
      type: 'TEXT',
      channel: payload.channel,
      message: payload.message,
      callouts: payload.callouts,
      date: payload.date,
      syncStatus,
    };

    return {
      ...state,
      list: {
        ...state.list,
        [payload.requestId]: newItem,
      },
      updates: updatesReducer(state.updates, payload.requestId, syncStatus),
    };
  }

  /**
   * @param {ChatMessagesListState} state
   * @param {ReduxAction<UpdateReadCountPayload>} action
   * @return {ChatMessagesListState}
   */
  function updateReadCountReducer(state, action) {
    if (!hasValidParentId(state, action))
      return state;

    /** @type {UpdateReadCountPayload} */
    const payload = action.payload;

    return {
      ...state,
      readCount: payload.readCount,
      isLoadingReadCount: false,
    };
  }
}



// WEBPACK FOOTER //
// ./src/js/comments/redux/chatMessageList.js